import { Link } from '@tanstack/react-router'
import { ArrowLeftIcon, PlusIcon, SearchIcon } from 'lucide-react'
import { useRef } from 'react'

import { Button, DataTable } from '@/components/ui'
import type { Table } from '@/components/ui/table/types'
import {
  CreateFolderDialog,
  createFolderColumns,
  DeleteFolderDialog,
  RenameFolderDialog,
  useFolders,
  useLoadingFolders,
} from '@/modules/folders'
import { useDialogActions } from '@/stores'
import { cn } from '@/utils'
import { useFetchDocuments } from '../hooks'
import type { Document } from '../stores'
import { DocumentProvider } from './DocumentContext'
import { DocumentsUpload } from './DocumentsUpload'
import { DeleteDocumentsDialog, EditDocumentDialog } from './dialogs'
import { ProcessDocumentsButton } from './ProcessButton'
import { DocumentsDataTable, documentColumns } from './tables'

interface DocumentsListProps {
  folderSlug?: string
}

export const DocumentsList = ({ folderSlug }: DocumentsListProps) => {
  const table = useRef<Table<Document> | null>(null)
  const folders = useFolders()
  const isLoadingFolders = useLoadingFolders()
  const { openDialog } = useDialogActions()
  useFetchDocuments(folderSlug)

  return (
    <>
      <CreateFolderDialog />
      <DeleteDocumentsDialog />
      <DeleteFolderDialog />
      <RenameFolderDialog />
      <DocumentProvider>
        <EditDocumentDialog />
      </DocumentProvider>

      <div className="space-y-6">
        <div className="flex items-start justify-end">
          <div className="flex items-center gap-2">
            <Button
              theme="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs font-medium"
              asChild
            >
              <Link
                to="/documents/search"
                search={folderSlug ? { folder: folderSlug } : undefined}
              >
                <SearchIcon className="size-3.5" />
                Search
              </Link>
            </Button>
          </div>
        </div>
        <div className="rounded-lg border overflow-hidden">
          <div>
            {!folderSlug && (
              <>
                <div className="bg-neutral-50 border-b px-4 py-2 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-blue-7 uppercase tracking-wide">
                    Folders
                  </h2>
                  <Button
                    size="sm"
                    className="gap-1.5 h-7 text-xs"
                    onClick={() =>
                      openDialog(CreateFolderDialog.id, { kind: 'document' })
                    }
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                    Create new folder
                  </Button>
                </div>

                <DataTable
                  isLoading={isLoadingFolders}
                  columns={createFolderColumns('document')}
                  data={folders}
                  height="h-auto"
                  emptyMessage="You don't have any folders yet"
                />
              </>
            )}

            <div
              className={cn(
                'bg-neutral-50 border-b px-4 py-2 flex items-center justify-between',
                !folderSlug && 'border-t justify-end',
              )}
            >
              {folderSlug && (
                <div className="flex items-center gap-2">
                  <Link
                    to="/documents"
                    aria-label="Back to all folders and documents"
                    className="text-muted-foreground hover:text-foreground hover:bg-neutral-200/60 rounded p-1 cursor-pointer transition-colors"
                  >
                    <ArrowLeftIcon className="size-4" />
                  </Link>
                  <h2 className="text-sm font-semibold text-blue-7 uppercase tracking-wide">
                    Documents
                  </h2>
                </div>
              )}
              <div className="flex gap-2">
                <ProcessDocumentsButton
                  table={table.current}
                  size="sm"
                  className="h-7 text-xs"
                >
                  Process documents
                </ProcessDocumentsButton>
              </div>
            </div>

            <DocumentsUpload />

            <DocumentsDataTable
              columns={documentColumns}
              onTableReady={(value) => {
                table.current = value
              }}
              table={table}
            />
          </div>
        </div>
      </div>
    </>
  )
}
